import React from 'react';
import {
  RouterProvider, createBrowserRouter, Outlet
} from 'react-router-dom';

import {
  dimensionsPermission,
  manageSurveyPermission,
  participantsReadPermission,
  participantsWritePermission,
  surveyOverviewPermission,
  surveyOverviewPermissionForAnySurvey,
  userAdminViewPermission,
  SurveyType,
  anySchemaPermission,
  surveySchemaPermission,
  extraSurveyPartPermission,
  surveyPartPermission,
  summaryPermission
} from 'unihow-common';
import { BASE_PATH, routes } from '../../constants';
import envConfig from '../../constants/env';
import styles from './main.module.css';
import { withLang, langType } from '../../i18n';

import PreviousLocationLayout from '../PreviousLocationLayout';
import Header from '../Header';
import Footer from '../Footer';
import GlobalPreloader from '../GlobalPreloader';
import Login from '../Login';
import MessageContainer from '../MessageContainer';
import RequireAuth from '../RequireAuth';
import FrontPage from '../FrontPage';
import ErrorPage from '../ErrorPage';
import PageNotFound from '../PageNotFound';
import UserFeedbackBox from '../UserFeedbackBox';
import Survey from '../Survey';
import SurveysLandingPage from '../SurveysLandingPage';
import ManageSurveys from '../ManageSurveys';
import RescheduleSurveyView from '../RescheduleSurveyView';
import SurveyStatistics from '../SurveyStatistics';
import SurveyParticipants from '../SurveyParticipants';
import AddSurveyParticipants from '../AddSurveyParticipants';
import SurveyReminder from '../SurveyReminder';
import ReopenSurveyView from '../ReopenSurveyView';
import CloseSurveyView from '../CloseSurveyView';
import StartSurvey from '../StartSurvey';
import SurveyQuestions from '../StartSurvey/SurveyQuestions';
import EditSurvey from '../EditSurvey';
import Contents from '../StartSurvey/Contents';
import Messages from '../StartSurvey/Messages';
import Participants from '../StartSurvey/Participants';
import ResearchConsent from '../StartSurvey/ResearchConsent';
import AddExtraSurveyPart from '../StartSurvey/AddExtraSurveyPart';
import Submitted from '../StartSurvey/Submitted';
import SurveySchemas from '../SurveySchemas';
import AddSurveySchemaParts from '../Editor/SurveySchemaEditor/AddSurveySchemaParts';
import SurveySchemaEditor from '../Editor/SurveySchemaEditor';
import SchemaPreview from '../Preview/SchemaPreview';
import SurveySchemaView from '../SurveySchemaView';
import SurveyPartEditor from '../Editor/SurveyPartEditor';
import SchemaPartPreview from '../Preview/SchemaPartPreview';
import SurveyPartView from '../SurveyPartView';
import SurveySummary from '../SurveySummary';
import ViewSurveyResponse from '../ViewSurveyResponse';
import SurveyTimeoutPage from '../SurveyTimeoutPage';
import User from '../PermissionManagement/User';
import AdminTools from '../AdminTools';
import DimensionStatistics from '../DimensionStatistics';
import AccessibilityStatement from '../AccessibilityStatement';
import Breadcrumb from '../Breadcrumb';
import {
  getExtraSurveyPart, getSurveyInfo, getSurveyResults, getSurveySchema, getSurveySchemaPart, getUserWithRolesAndPermissions
} from '../../api';
import Schemas from '../SurveySchemas/Schemas';
import SurveyParts from '../SurveySchemas/SurveyParts';
import ExtraSurveyParts from '../SurveySchemas/ExtraSurveyParts';
import Introduction from '../Survey/Introduction';
import SurveyPart from '../Survey/SurveyPart';
import RespondentResearchConsent from '../Survey/ResearchConsent';
import ResponseSuccess from '../Survey/ResponseSuccess';
import ActivityLog from '../ActivityLog';
import PermissionManagement from '../PermissionManagement';
import MatomoPageViewTracker from '../Matomo/MatomoPageViewTracker';
import getRoles from '../../api/roles';

const Layout = ({ translate, lang }) => (
  <div className={styles.appContainer}>
    { envConfig.MATOMO_ENABLED && <MatomoPageViewTracker /> }
    <GlobalPreloader />
    <Header />
    <MessageContainer translate={translate} />
    <main className={styles.routeViewContainer}>
      <Breadcrumb translate={translate} lang={lang} />
      <Outlet />
    </main>
    <UserFeedbackBox />
    <Footer />
  </div>
);

// I'm using comments here to make it easier for the eyes to
// see which route is where!
const router = (translate, lang) => createBrowserRouter([
  {
    path: routes.index,
    element: <Layout translate={translate} lang={lang} />,
    children: [
      {
        element: <PreviousLocationLayout />,
        children: [
          {
            element: <RequireAuth />,
            children: [
              {
                index: true,
                element: <FrontPage />
              },
              { // survey/:id/*
                path: routes.survey.index,
                handle: {
                  crumb: () => ({ content: 'frontPage.surveyBoxList.answerSurvey', isTranslationKey: true })
                },
                element: <Survey />,
                children: [
                  { // survey/:id/introduction
                    path: routes.survey.introduction,
                    element: <Introduction />
                  },
                  { // survey/:id/page/:id
                    path: routes.survey.surveyPart,
                    element: <SurveyPart />
                  },
                  { // survey/:id/research-consent
                    path: routes.survey.researchConsent,
                    element: <RespondentResearchConsent />
                  },
                  { // survey/:id/response-success
                    path: routes.survey.responseSuccess,
                    element: <ResponseSuccess />
                  }
                ]
              },
              { // survey/:id/feedback/*
                path: routes.survey.feedback.index,
                handle: {
                  crumb: () => ({ content: 'surveySummary.title', isTranslationKey: true })
                },
                children: [
                  {
                    index: true,
                    element: <SurveySummary />
                  },
                  { // survey/:id/feedback/responses
                    path: routes.survey.feedback.responses,
                    handle: {
                      crumb: () => ({ content: 'viewSurveyResponse.title', isTranslationKey: true })
                    },
                    element: <ViewSurveyResponse />
                  }
                ]
              },
              { // survey-submitted
                path: routes.surveySubmitted,
                element: <Submitted />
              },
              { // survey-timeout
                path: routes.surveyTimeout,
                element: <SurveyTimeoutPage />
              },
              {
                path: '/*',
                element: <PageNotFound translate={translate} />
              }
            ]
          },
          {
            element: <RequireAuth permissions={[userAdminViewPermission()]} />,
            children: [
              {
                path: routes.adminTools.index,
                handle: {
                  crumb: () => ({ content: 'adminTools.title', isTranslationKey: true })
                },
                element: <AdminTools />,
                children: [
                  {
                    path: routes.adminTools.activityLog,
                    handle: {
                      crumb: () => ({ content: 'adminTools.activityLog', isTranslationKey: true })
                    },
                    element: <ActivityLog />
                  },
                  {
                    path: routes.adminTools.permissions.index,
                    handle: {
                      crumb: () => ({ content: 'adminTools.permissions', isTranslationKey: true })
                    },
                    children: [
                      {
                        index: true,
                        element: <PermissionManagement />
                      },
                      {
                        path: routes.adminTools.permissions.manageUsers.user,
                        loader: async ({ params }) => {
                          const [user, roles] = await Promise.all([
                            getUserWithRolesAndPermissions(params.userId),
                            getRoles()
                          ]);
                          return { user, roles };
                        },
                        handle: {
                          crumb: (data) => ({ content: data.user?.commonName })
                        },
                        element: <User />
                      }
                    ]
                  }
                ]
              }
            ]
          },
          { // login
            path: routes.login,
            element: <Login />
          },
          { // accessability-statement
            path: routes.accessibilityStatement,
            handle: {
              crumb: () => ({ content: 'footer.accessibilityStatement', isTranslationKey: true })
            },
            element: <AccessibilityStatement />
          },
          { // error/:errorCode
            path: `${routes.error}/:errorCode?`,
            element: <ErrorPage />
          },
          { // surveys/*
            path: routes.surveys.index,
            handle: {
              crumb: () => ({ content: 'surveysLandingPage.title', isTranslationKey: true })
            },
            children: [
              {
                element: <RequireAuth permissions={surveyOverviewPermissionForAnySurvey()} />,
                children: [
                  {
                    index: true,
                    element: <SurveysLandingPage />
                  }
                ]
              },
              {
                element: <RequireAuth permissions={[surveyOverviewPermission(SurveyType.DEGREE_PROGRAM_SURVEY)]} />,
                children: [
                  { // surveys/hul-surveys/*
                    path: routes.surveys.hulSurveys.index,
                    handle: {
                      crumb: () => ({ content: 'manageSurveys.hulSurveys', isTranslationKey: true })
                    },
                    children: [
                      {
                        index: true,
                        element: <ManageSurveys type={SurveyType.DEGREE_PROGRAM_SURVEY} />
                      },
                      { // surveys/hul-surveys/:id/*
                        path: routes.surveys.hulSurveys.survey.index,
                        loader: ({ params }) => getSurveyInfo(params.surveyId),
                        handle: {
                          crumb: (data) => ({ content: data.title })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyStatistics surveyType={SurveyType.DEGREE_PROGRAM_SURVEY} />
                          },
                          { // surveys/hul-surveys/:id/participants/*
                            element: <RequireAuth permissions={[
                              participantsReadPermission(SurveyType.DEGREE_PROGRAM_SURVEY),
                              manageSurveyPermission(SurveyType.DEGREE_PROGRAM_SURVEY)]}
                            />,
                            children: [
                              {
                                path: routes.surveys.hulSurveys.survey.participants.index,
                                handle: {
                                  crumb: () => ({ content: 'survey.participants', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyParticipants />
                                  },
                                  { // surveys/hul-surveys/:id/participants/edit
                                    element: <RequireAuth permissions={[
                                      participantsWritePermission(SurveyType.DEGREE_PROGRAM_SURVEY),
                                      manageSurveyPermission(SurveyType.DEGREE_PROGRAM_SURVEY)
                                    ]}
                                    />,
                                    children: [
                                      {
                                        path: routes.surveys.hulSurveys.survey.participants.edit,
                                        element: <AddSurveyParticipants />
                                      }
                                    ]
                                  }
                                ]
                              }
                            ]
                          },
                          {
                            element: <RequireAuth permissions={[
                              dimensionsPermission(SurveyType.DEGREE_PROGRAM_SURVEY),
                              manageSurveyPermission(SurveyType.DEGREE_PROGRAM_SURVEY),
                              summaryPermission(SurveyType.DEGREE_PROGRAM_SURVEY)
                            ]}
                            />,
                            handle: {
                              crumb: () => ({ content: 'surveyStatistics.dimensions', isTranslationKey: true })
                            },
                            children: [
                              {
                                path: routes.surveys.hulSurveys.survey.dimension,
                                loader: async ({ params }) => ({ ...await getSurveyResults(params.surveyId), params }),
                                handle: {
                                  crumb: (data) => ({ content: data.variableStatistics[data.params.dimensionId].name })
                                },
                                element: <DimensionStatistics />
                              }
                            ]
                          },
                          {
                            element: <RequireAuth permissions={[manageSurveyPermission(SurveyType.DEGREE_PROGRAM_SURVEY)]} />,
                            children: [
                              { // surveys/hul-surveys/:id/close
                                path: routes.surveys.hulSurveys.survey.close,
                                handle: {
                                  crumb: () => ({ content: 'survey.close', isTranslationKey: true })
                                },
                                element: <CloseSurveyView type={SurveyType.DEGREE_PROGRAM_SURVEY} />
                              },
                              { // surveys/hul-surveys/:id/edit/*
                                path: routes.surveys.hulSurveys.survey.edit.index,
                                element: <EditSurvey />,
                                children: [
                                  { // surveys/hul-surveys/:id/edit/contents/*
                                    path: routes.surveys.hulSurveys.survey.edit.contents.index,
                                    handle: {
                                      crumb: () => ({ content: 'editSurvey.title', isTranslationKey: true })
                                    },
                                    children: [
                                      {
                                        index: true,
                                        element: <Contents />
                                      },
                                      { // surveys/hul-surveys/:id/contents/extra-survey-part
                                        path: routes.surveys.hulSurveys.survey.edit.contents.extraSurveyPart,
                                        handle: {
                                          crumb: () => ({ content: 'surveySchemaParts.extraSurveyPart', isTranslationKey: true })
                                        },
                                        element: <AddExtraSurveyPart />
                                      },
                                      { // surveys/hul-surveys/:id/contents/questions
                                        path: routes.surveys.hulSurveys.survey.edit.contents.questions,
                                        handle: {
                                          crumb: () => ({ content: 'surveyParts.questions', isTranslationKey: true })
                                        },
                                        element: <SurveyQuestions />
                                      }
                                    ]
                                  },
                                  { // surveys/hul-surveys/:id/edit/messages
                                    path: routes.surveys.hulSurveys.survey.edit.messages,
                                    element: <Messages />
                                  },
                                  { // surveys/hul-surveys/:id//edit/participants
                                    path: routes.surveys.hulSurveys.survey.edit.participants,
                                    element: <Participants />
                                  },
                                  { // surveys/hul-surveys/:id/edit/research-consent
                                    path: routes.surveys.hulSurveys.survey.edit.researchConsent,
                                    element: <ResearchConsent />
                                  }
                                ]
                              },
                              { // surveys/hul-surveys/:id/reschedule
                                path: routes.surveys.hulSurveys.survey.reschedule,
                                handle: {
                                  crumb: () => ({ content: 'startSurvey.scheduling', isTranslationKey: true })
                                },
                                element: <RescheduleSurveyView />
                              },
                              { // surveys/hul-surveys/:id/reminder
                                path: routes.surveys.hulSurveys.survey.reminder,
                                handle: {
                                  crumb: () => ({ content: 'surveyReminder.title', isTranslationKey: true })
                                },
                                element: <SurveyReminder />
                              },
                              { // surveys/hul-surveys/:id/reopen
                                path: routes.surveys.hulSurveys.survey.reopen,
                                handle: {
                                  crumb: () => ({ content: 'manageSurveys.reopen.title', isTranslationKey: true })
                                },
                                element: <ReopenSurveyView />
                              }
                            ]
                          }
                        ]
                      },
                      { // surveys/hul-surveys/start-survey/*
                        element: <RequireAuth permissions={[manageSurveyPermission(SurveyType.DEGREE_PROGRAM_SURVEY)]} />,
                        children: [
                          {
                            path: routes.surveys.hulSurveys.startSurvey.index,
                            handle: {
                              crumb: () => ({ content: 'startSurvey.startSurvey', isTranslationKey: true })
                            },
                            element: <StartSurvey />,
                            children: [
                              { // surveys/hul-surveys/start-survey/contents/*
                                path: routes.surveys.hulSurveys.startSurvey.contents.index,
                                children: [
                                  {
                                    index: true,
                                    element: <Contents />
                                  },
                                  { // surveys/hul-surveys/start-survey/contents/questions
                                    path: routes.surveys.hulSurveys.startSurvey.contents.questions,
                                    element: <SurveyQuestions />
                                  },
                                  { // surveys/hul-surveys/start-survey/contents/extra-survey-part
                                    path: routes.surveys.hulSurveys.startSurvey.contents.extraSurveyPart,
                                    element: <AddExtraSurveyPart />
                                  }
                                ]
                              },
                              { // surveys/hul-surveys/start-survey/messages
                                path: routes.surveys.hulSurveys.startSurvey.messages,
                                element: <Messages />
                              },
                              { // surveys/hul-surveys/start-survey/participants
                                path: routes.surveys.hulSurveys.startSurvey.participants,
                                element: <Participants />
                              },
                              { // surveys/hul-surveys/start-survey/research-consent
                                path: routes.surveys.hulSurveys.startSurvey.researchConsent,
                                element: <ResearchConsent />
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                ]
              },
              {
                element: <RequireAuth permissions={[surveyOverviewPermission(SurveyType.HUP)]} />,
                children: [
                  { // surveys/hup-surveys/*
                    path: routes.surveys.hupSurveys.index,
                    handle: {
                      crumb: () => ({ content: 'manageSurveys.hupSurveys', isTranslationKey: true })
                    },
                    children: [
                      {
                        index: true,
                        element: <ManageSurveys type={SurveyType.HUP} />
                      },
                      { // surveys/hup-surveys/:id/*
                        path: routes.surveys.hupSurveys.survey.index,
                        loader: ({ params }) => getSurveyInfo(params.surveyId),
                        handle: {
                          crumb: (data) => ({ content: data.title })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyStatistics surveyType={SurveyType.HUP} />
                          },
                          { // surveys/hup-surveys/:id/participants/*
                            element: <RequireAuth permissions={[
                              participantsReadPermission(SurveyType.HUP),
                              manageSurveyPermission(SurveyType.HUP)
                            ]}
                            />,
                            children: [
                              {
                                path: routes.surveys.hupSurveys.survey.participants.index,
                                handle: {
                                  crumb: () => ({ content: 'survey.participants', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyParticipants />
                                  },
                                  { // surveys/hup-surveys/:id/participants/edit
                                    element: <RequireAuth permissions={[
                                      participantsWritePermission(SurveyType.HUP),
                                      manageSurveyPermission(SurveyType.HUP)
                                    ]}
                                    />,
                                    children: [
                                      {
                                        path: routes.surveys.hupSurveys.survey.participants.edit,
                                        element: <AddSurveyParticipants />
                                      }
                                    ]
                                  }
                                ]
                              }
                            ]
                          },
                          {
                            element: <RequireAuth permissions={[
                              dimensionsPermission(SurveyType.HUP),
                              manageSurveyPermission(SurveyType.HUP),
                              summaryPermission(SurveyType.HUP)
                            ]}
                            />,
                            handle: {
                              crumb: () => ({ content: 'surveyStatistics.dimensions', isTranslationKey: true })
                            },
                            children: [
                              {
                                path: routes.surveys.hupSurveys.survey.dimension,
                                loader: async ({ params }) => ({ ...await getSurveyResults(params.surveyId), params }),
                                handle: {
                                  crumb: (data) => ({ content: data.variableStatistics[data.params.dimensionId].name })
                                },
                                element: <DimensionStatistics />
                              }
                            ]
                          },
                          {
                            element: <RequireAuth permissions={[manageSurveyPermission(SurveyType.HUP)]} />,
                            children: [
                              { // surveys/hup-surveys/:id/close
                                path: routes.surveys.hupSurveys.survey.close,
                                handle: {
                                  crumb: () => ({ content: 'survey.close', isTranslationKey: true })
                                },
                                element: <CloseSurveyView type={SurveyType.HUP} />
                              },
                              { // surveys/hup-surveys/:id/edit/*
                                path: routes.surveys.hupSurveys.survey.edit.index,
                                element: <EditSurvey />,
                                children: [
                                  { // surveys/hup-surveys/:id/edit/contents/*
                                    path: routes.surveys.hupSurveys.survey.edit.contents.index,
                                    handle: {
                                      crumb: () => ({ content: 'editSurvey.title', isTranslationKey: true })
                                    },
                                    children: [
                                      {
                                        index: true,
                                        element: <Contents />
                                      },
                                      { // surveys/hup-surveys/:id/contents/extra-survey-part
                                        path: routes.surveys.hupSurveys.survey.edit.contents.extraSurveyPart,
                                        handle: {
                                          crumb: () => ({ content: 'surveySchemaParts.extraSurveyPart', isTranslationKey: true })
                                        },
                                        element: <AddExtraSurveyPart />
                                      },
                                      { // surveys/hup-surveys/:id/contents/questions
                                        path: routes.surveys.hupSurveys.survey.edit.contents.questions,
                                        handle: {
                                          crumb: () => ({ content: 'surveyParts.questions', isTranslationKey: true })
                                        },
                                        element: <SurveyQuestions />
                                      }
                                    ]
                                  },
                                  { // surveys/hup-surveys/:id/edit/messages
                                    path: routes.surveys.hupSurveys.survey.edit.messages,
                                    element: <Messages />
                                  },
                                  { // surveys/hup-surveys/:id//edit/participants
                                    path: routes.surveys.hupSurveys.survey.edit.participants,
                                    element: <Participants />
                                  },
                                  { // surveys/hup-surveys/:id/edit/research-consent
                                    path: routes.surveys.hupSurveys.survey.edit.researchConsent,
                                    element: <ResearchConsent />
                                  }
                                ]
                              },
                              { // surveys/hup-surveys/:id/reschedule
                                path: routes.surveys.hupSurveys.survey.reschedule,
                                handle: {
                                  crumb: () => ({ content: 'startSurvey.scheduling', isTranslationKey: true })
                                },
                                element: <RescheduleSurveyView />
                              },
                              { // surveys/hup-surveys/:id/reminder
                                path: routes.surveys.hupSurveys.survey.reminder,
                                handle: {
                                  crumb: () => ({ content: 'surveyReminder.title', isTranslationKey: true })
                                },
                                element: <SurveyReminder />
                              },
                              { // surveys/hup-surveys/:id/reopen
                                path: routes.surveys.hupSurveys.survey.reopen,
                                handle: {
                                  crumb: () => ({ content: 'manageSurveys.reopen.title', isTranslationKey: true })
                                },
                                element: <ReopenSurveyView />
                              }
                            ]
                          }
                        ]
                      },
                      { // surveys/hup-surveys/start-survey/*
                        element: <RequireAuth permissions={[manageSurveyPermission(SurveyType.HUP)]} />,
                        children: [
                          {
                            path: routes.surveys.hupSurveys.startSurvey.index,
                            handle: {
                              crumb: () => ({ content: 'startSurvey.startSurvey', isTranslationKey: true })
                            },
                            element: <StartSurvey />,
                            children: [
                              { // surveys/hup-surveys/start-survey/contents/*
                                path: routes.surveys.hupSurveys.startSurvey.contents.index,
                                children: [
                                  {
                                    index: true,
                                    element: <Contents />
                                  },
                                  { // surveys/hup-surveys/start-survey/contents/questions
                                    path: routes.surveys.hupSurveys.startSurvey.contents.questions,
                                    element: <SurveyQuestions />
                                  },
                                  { // surveys/hup-surveys/start-survey/contents/extra-survey-part
                                    path: routes.surveys.hupSurveys.startSurvey.contents.extraSurveyPart,
                                    element: <AddExtraSurveyPart />
                                  }
                                ]
                              },
                              { // surveys/hup-surveys/start-survey/messages
                                path: routes.surveys.hupSurveys.startSurvey.messages,
                                element: <Messages />
                              },
                              { // surveys/hup-surveys/start-survey/participants
                                path: routes.surveys.hupSurveys.startSurvey.participants,
                                element: <Participants />
                              },
                              { // surveys/hup-surveys/start-survey/research-consent
                                path: routes.surveys.hupSurveys.startSurvey.researchConsent,
                                element: <ResearchConsent />
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                ]
              },
              {
                element: <RequireAuth permissions={[surveyOverviewPermission(SurveyType.HUT)]} />,
                children: [
                  { // surveys/hut-surveys/*
                    path: routes.surveys.hutSurveys.index,
                    handle: {
                      crumb: () => ({ content: 'manageSurveys.hutSurveys', isTranslationKey: true })
                    },
                    children: [
                      {
                        index: true,
                        element: <ManageSurveys type={SurveyType.HUT} />
                      },
                      { // surveys/hut-surveys/:id/*
                        path: routes.surveys.hutSurveys.survey.index,
                        loader: ({ params }) => getSurveyInfo(params.surveyId),
                        handle: {
                          crumb: (data) => ({ content: data.title })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyStatistics surveyType={SurveyType.HUT} />
                          },
                          { // surveys/hut-surveys/:id/participants/*
                            element: <RequireAuth permissions={[
                              participantsReadPermission(SurveyType.HUT),
                              manageSurveyPermission(SurveyType.HUT)
                            ]}
                            />,
                            children: [
                              {
                                path: routes.surveys.hutSurveys.survey.participants.index,
                                handle: {
                                  crumb: () => ({ content: 'survey.participants', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyParticipants />
                                  },
                                  { // surveys/hut-surveys/:id/participants/edit
                                    element: <RequireAuth permissions={[
                                      participantsWritePermission(SurveyType.HUT),
                                      manageSurveyPermission(SurveyType.HUT)
                                    ]}
                                    />,
                                    children: [
                                      {
                                        path: routes.surveys.hutSurveys.survey.participants.edit,
                                        element: <AddSurveyParticipants />
                                      }
                                    ]
                                  }
                                ]
                              }
                            ]
                          },
                          {
                            element: <RequireAuth permissions={[
                              dimensionsPermission(SurveyType.HUT),
                              manageSurveyPermission(SurveyType.HUT),
                              summaryPermission(SurveyType.HUT)
                            ]}
                            />,
                            handle: {
                              crumb: () => ({ content: 'surveyStatistics.dimensions', isTranslationKey: true })
                            },
                            children: [
                              {
                                path: routes.surveys.hutSurveys.survey.dimension,
                                loader: async ({ params }) => ({ ...await getSurveyResults(params.surveyId), params }),
                                handle: {
                                  crumb: (data) => ({ content: data.variableStatistics[data.params.dimensionId].name })
                                },
                                element: <DimensionStatistics />
                              }
                            ]
                          },
                          {
                            element: <RequireAuth permissions={[manageSurveyPermission(SurveyType.HUT)]} />,
                            children: [
                              { // surveys/hut-surveys/:id/close
                                path: routes.surveys.hutSurveys.survey.close,
                                handle: {
                                  crumb: () => ({ content: 'survey.close', isTranslationKey: true })
                                },
                                element: <CloseSurveyView type={SurveyType.HUT} />
                              },
                              { // surveys/hut-surveys/:id/edit/*
                                path: routes.surveys.hutSurveys.survey.edit.index,
                                element: <EditSurvey />,
                                children: [
                                  { // surveys/hut-surveys/:id/edit/contents/*
                                    path: routes.surveys.hutSurveys.survey.edit.contents.index,
                                    handle: {
                                      crumb: () => ({ content: 'editSurvey.title', isTranslationKey: true })
                                    },
                                    children: [
                                      {
                                        index: true,
                                        element: <Contents />
                                      },
                                      { // surveys/hut-surveys/:id/contents/extra-survey-part
                                        path: routes.surveys.hutSurveys.survey.edit.contents.extraSurveyPart,
                                        handle: {
                                          crumb: () => ({ content: 'surveySchemaParts.extraSurveyPart', isTranslationKey: true })
                                        },
                                        element: <AddExtraSurveyPart />
                                      },
                                      { // surveys/hut-surveys/:id/contents/questions
                                        path: routes.surveys.hutSurveys.survey.edit.contents.questions,
                                        handle: {
                                          crumb: () => ({ content: 'surveyParts.questions', isTranslationKey: true })
                                        },
                                        element: <SurveyQuestions />
                                      }
                                    ]
                                  },
                                  { // surveys/hut-surveys/:id/edit/messages
                                    path: routes.surveys.hutSurveys.survey.edit.messages,
                                    element: <Messages />
                                  },
                                  { // surveys/hut-surveys/:id//edit/participants
                                    path: routes.surveys.hutSurveys.survey.edit.participants,
                                    element: <Participants />
                                  },
                                  { // surveys/hut-surveys/:id/edit/research-consent
                                    path: routes.surveys.hutSurveys.survey.edit.researchConsent,
                                    element: <ResearchConsent />
                                  }
                                ]
                              },
                              { // surveys/hut-surveys/:id/reschedule
                                path: routes.surveys.hutSurveys.survey.reschedule,
                                handle: {
                                  crumb: () => ({ content: 'startSurvey.scheduling', isTranslationKey: true })
                                },
                                element: <RescheduleSurveyView />
                              },
                              { // surveys/hut-surveys/:id/reminder
                                path: routes.surveys.hutSurveys.survey.reminder,
                                handle: {
                                  crumb: () => ({ content: 'surveyReminder.title', isTranslationKey: true })
                                },
                                element: <SurveyReminder />
                              },
                              { // surveys/hut-surveys/:id/reopen
                                path: routes.surveys.hutSurveys.survey.reopen,
                                handle: {
                                  crumb: () => ({ content: 'manageSurveys.reopen.title', isTranslationKey: true })
                                },
                                element: <ReopenSurveyView />
                              }
                            ]
                          }
                        ]
                      },
                      { // surveys/hut-surveys/start-survey/*
                        element: <RequireAuth permissions={[manageSurveyPermission(SurveyType.HUT)]} />,
                        children: [
                          {
                            path: routes.surveys.hutSurveys.startSurvey.index,
                            handle: {
                              crumb: () => ({ content: 'startSurvey.startSurvey', isTranslationKey: true })
                            },
                            element: <StartSurvey />,
                            children: [
                              { // surveys/hut-surveys/start-survey/contents/*
                                path: routes.surveys.hutSurveys.startSurvey.contents.index,
                                children: [
                                  {
                                    index: true,
                                    element: <Contents />
                                  },
                                  { // surveys/hut-surveys/start-survey/contents/questions
                                    path: routes.surveys.hutSurveys.startSurvey.contents.questions,
                                    element: <SurveyQuestions />
                                  },
                                  { // surveys/hut-surveys/start-survey/contents/extra-survey-part
                                    path: routes.surveys.hutSurveys.startSurvey.contents.extraSurveyPart,
                                    element: <AddExtraSurveyPart />
                                  }
                                ]
                              },
                              { // surveys/hut-surveys/start-survey/messages
                                path: routes.surveys.hutSurveys.startSurvey.messages,
                                element: <Messages />
                              },
                              { // surveys/hut-surveys/start-survey/participants
                                path: routes.surveys.hutSurveys.startSurvey.participants,
                                element: <Participants />
                              },
                              { // surveys/hut-surveys/start-survey/research-consent
                                path: routes.surveys.hutSurveys.startSurvey.researchConsent,
                                element: <ResearchConsent />
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                ]
              },
              {
                element: <RequireAuth permissions={[surveyOverviewPermission(SurveyType.COURSE_SURVEY)]} />,
                children: [
                  { // surveys/hus-surveys/*
                    path: routes.surveys.husSurveys.index,
                    handle: {
                      crumb: () => ({ content: 'manageSurveys.husSurveys', isTranslationKey: true })
                    },
                    children: [
                      {
                        index: true,
                        element: <ManageSurveys type={SurveyType.COURSE_SURVEY} />
                      },
                      { // surveys/hus-surveys/:id/*
                        path: routes.surveys.husSurveys.survey.index,
                        loader: ({ params }) => getSurveyInfo(params.surveyId),
                        handle: {
                          crumb: (data) => ({ content: data.title })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyStatistics surveyType={SurveyType.COURSE_SURVEY} />
                          },
                          { // surveys/hus-surveys/:id/close
                            path: routes.surveys.husSurveys.survey.close,
                            handle: {
                              crumb: () => ({ content: 'survey.close', isTranslationKey: true })
                            },
                            element: <CloseSurveyView type={SurveyType.COURSE_SURVEY} />
                          },
                          { // surveys/hus-surveys/:id/edit/*
                            path: routes.surveys.husSurveys.survey.edit.index,
                            element: <EditSurvey />,
                            handle: {
                              crumb: () => ({ content: 'editSurvey.title', isTranslationKey: true })
                            },
                            children: [
                              { // surveys/hus-surveys/:id/edit/contents
                                path: routes.surveys.husSurveys.survey.edit.contents.index,
                                element: <Contents />
                              },
                              { // surveys/hus-surveys/:id/edit/messages
                                path: routes.surveys.husSurveys.survey.edit.messages,
                                element: <Messages />
                              },
                              { // surveys/hus-surveys/:id/edit/participants
                                path: routes.surveys.husSurveys.survey.edit.participants,
                                element: <Participants />
                              }
                            ]
                          }
                        ]
                      },
                      { // surveys/hus-surveys/start-survey/*
                        path: routes.surveys.husSurveys.startSurvey.index,
                        handle: {
                          crumb: () => ({ content: 'startSurvey.startSurvey', isTranslationKey: true })
                        },
                        element: <StartSurvey />,
                        children: [
                          { // surveys/hus-surveys/start-survey/contents
                            path: routes.surveys.husSurveys.startSurvey.contents.index,
                            element: <Contents />
                          },
                          { // surveys/hus-surveys/start-survey/messages
                            path: routes.surveys.husSurveys.startSurvey.messages,
                            element: <Messages />
                          },
                          { // surveys/hus-surveys/start-survey/participants
                            path: routes.surveys.husSurveys.startSurvey.participants,
                            element: <Participants />
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            element: <RequireAuth permissions={[anySchemaPermission()]} />,
            children: [
              { // survey-schemas/*
                path: routes.surveySchemas.index,
                handle: {
                  crumb: () => ({ content: 'surveySchemas.title', isTranslationKey: true })
                },
                element: <SurveySchemas />,
                children: [
                  { // survey-schemas/schemas
                    path: routes.surveySchemas.schemas.index,
                    children: [
                      {
                        index: true,
                        element: <Schemas translate={translate} />
                      },
                      { // survey-schemas/schemas/:id
                        path: routes.surveySchemas.schemas.schema.index,
                        loader: ({ params }) => getSurveySchema(params.surveySchemaId),
                        handle: {
                          crumb: (data) => ({ content: Object.keys(data.title).length ? data.title : data.code })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveySchemaView />
                          },
                          {
                            element: <RequireAuth permissions={[surveySchemaPermission(true)]} />,
                            children: [
                              { // survey-schemas/schemas/:id/edit
                                path: routes.surveySchemas.schemas.schema.edit.index,
                                handle: {
                                  crumb: () => ({ content: 'editor.surveySchema.edit.infoTitle', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveySchemaEditor />
                                  },
                                  { // survey-schemas/schemas/:id/edit/survey-parts
                                    path: routes.surveySchemas.schemas.schema.edit.surveyParts,
                                    handle: {
                                      crumb: () => ({ content: 'startSurvey.surveyParts.title', isTranslationKey: true })
                                    },
                                    element: <AddSurveySchemaParts />
                                  },
                                  { // survey-schemas/schemas/:id/edit/extra-survey-part
                                    path: routes.surveySchemas.schemas.schema.edit.extraSurveyPart,
                                    handle: {
                                      crumb: () => ({ content: 'extraSurveyParts.title', isTranslationKey: true })
                                    },
                                    element: <AddExtraSurveyPart toSchema />
                                  },
                                  { // survey-schemas/schemas/:id/edit/preview
                                    path: routes.surveySchemas.schemas.schema.edit.preview,
                                    handle: {
                                      crumb: () => ({ content: 'common.preview', isTranslationKey: true })
                                    },
                                    element: <SchemaPreview />
                                  }
                                ]
                              },
                              { // survey-schemas/schemas/:id/copy
                                path: routes.surveySchemas.schemas.schema.copy.index,
                                handle: {
                                  crumb: () => ({ content: 'actions.createNewCopy', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveySchemaEditor />
                                  },
                                  { // survey-schemas/schemas/:id/copy/survey-parts
                                    path: routes.surveySchemas.schemas.schema.copy.surveyParts,
                                    handle: {
                                      crumb: () => ({ content: 'startSurvey.surveyParts.title', isTranslationKey: true })
                                    },
                                    element: <AddSurveySchemaParts />
                                  },
                                  { // survey-schemas/schemas/:id/copy/extra-survey-part
                                    path: routes.surveySchemas.schemas.schema.copy.extraSurveyPart,
                                    handle: {
                                      crumb: () => ({ content: 'extraSurveyParts.title', isTranslationKey: true })
                                    },
                                    element: <AddExtraSurveyPart toSchema />
                                  },
                                  { // survey-schemas/schemas/:id/copy/preview
                                    path: routes.surveySchemas.schemas.schema.copy.preview,
                                    handle: {
                                      crumb: () => ({ content: 'common.preview', isTranslationKey: true })
                                    },
                                    element: <SchemaPreview />
                                  }
                                ]
                              },
                              { // survey-schemas/schemas/:id/new-version/*
                                path: routes.surveySchemas.schemas.schema.newVersion.index,
                                handle: {
                                  crumb: () => ({ content: 'actions.createNewVersion', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveySchemaEditor />
                                  },
                                  { // survey-schemas/schemas/:id/new-version/survey-parts
                                    path: routes.surveySchemas.schemas.schema.newVersion.surveyParts,
                                    handle: {
                                      crumb: () => ({ content: 'startSurvey.surveyParts.title', isTranslationKey: true })
                                    },
                                    element: <AddSurveySchemaParts />
                                  },
                                  { // survey-schemas/schemas/:id/new-version/extra-survey-part
                                    path: routes.surveySchemas.schemas.schema.newVersion.extraSurveyPart,
                                    handle: {
                                      crumb: () => ({ content: 'extraSurveyParts.title', isTranslationKey: true })
                                    },
                                    element: <AddExtraSurveyPart toSchema />
                                  },
                                  { // survey-schemas/schemas/:id/new-version/preview
                                    path: routes.surveySchemas.schemas.schema.newVersion.preview,
                                    handle: {
                                      crumb: () => ({ content: 'common.preview', isTranslationKey: true })
                                    },
                                    element: <SchemaPreview />
                                  }
                                ]
                              }
                            ]
                          }
                        ]
                      },
                      { // survey-schemas/schemas/create/*
                        path: routes.surveySchemas.schemas.create.index,
                        element: <RequireAuth permissions={[surveySchemaPermission(true)]} />,
                        handle: {
                          crumb: () => ({ content: 'surveySchemas.createNew', isTranslationKey: true })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveySchemaEditor />
                          },
                          { // survey-schemas/schemas/create/survey-parts
                            path: routes.surveySchemas.schemas.create.surveyParts,
                            handle: {
                              crumb: () => ({ content: 'startSurvey.surveyParts.title', isTranslationKey: true })
                            },
                            element: <AddSurveySchemaParts />
                          },
                          { // survey-schemas/schemas/create/extra-survey-part
                            path: routes.surveySchemas.schemas.create.extraSurveyPart,
                            handle: {
                              crumb: () => ({ content: 'extraSurveyParts.title', isTranslationKey: true })
                            },
                            element: <AddExtraSurveyPart toSchema />
                          },
                          { // survey-schemas/schemas/create/preview
                            path: routes.surveySchemas.schemas.create.preview,
                            handle: {
                              crumb: () => ({ content: 'common.preview', isTranslationKey: true })
                            },
                            element: <SchemaPreview />
                          }
                        ]
                      }
                    ]
                  },
                  { // survey-schemas/survey-parts/*
                    path: routes.surveySchemas.surveyParts.index,
                    handle: {
                      crumb: () => ({ content: 'surveySchemaParts.schemaParts', isTranslationKey: true })
                    },
                    children: [
                      {
                        index: true,
                        element: <SurveyParts translate={translate} />
                      },
                      { // survey-schemas/survey-parts/:id/*
                        path: routes.surveySchemas.surveyParts.surveyPart.index,
                        loader: ({ params }) => getSurveySchemaPart(params.surveyPartId),
                        handle: {
                          crumb: (data) => ({ content: data.title })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyPartView />
                          },
                          {
                            element: <RequireAuth permissions={[surveyPartPermission(true)]} />,
                            children: [
                              { // survey-schemas/survey-parts/:id/edit/*
                                path: routes.surveySchemas.surveyParts.surveyPart.edit.index,
                                handle: {
                                  crumb: () => ({ content: 'actions.edit', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyPartEditor />
                                  },
                                  { // survey-schemas/survey-parts/:id/edit/preview
                                    path: routes.surveySchemas.surveyParts.surveyPart.edit.preview,
                                    element: <SchemaPartPreview />
                                  }
                                ]
                              },
                              { // survey-schemas/survey-parts/:id/copy/*
                                path: routes.surveySchemas.surveyParts.surveyPart.copy.index,
                                handle: {
                                  crumb: () => ({ content: 'actions.createNewCopy', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyPartEditor />
                                  },
                                  { // survey-schemas/survey-parts/:id/copy/preview
                                    path: routes.surveySchemas.surveyParts.surveyPart.copy.preview,
                                    element: <SchemaPartPreview />
                                  }
                                ]
                              },
                              { // survey-schemas/survey-parts/:id/new-version/*
                                path: routes.surveySchemas.surveyParts.surveyPart.newVersion.index,
                                handle: {
                                  crumb: () => ({ content: 'actions.createNewVersion', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyPartEditor />
                                  },
                                  { // survey-schemas/survey-parts/:id/new-version/preview
                                    path: routes.surveySchemas.surveyParts.surveyPart.newVersion.preview,
                                    element: <SchemaPartPreview />
                                  }
                                ]
                              }
                            ]
                          }
                        ]
                      },
                      { // survey-schemas/survey-parts/create/*
                        path: routes.surveySchemas.surveyParts.create.index,
                        element: <RequireAuth permissions={[surveyPartPermission(true)]} />,
                        handle: {
                          crumb: () => ({ content: 'surveySchemaParts.createNew', isTranslationKey: true })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyPartEditor />
                          },
                          { // survey-schemas/survey-parts/create/preview
                            path: routes.surveySchemas.surveyParts.create.preview,
                            element: <SchemaPartPreview />
                          }
                        ]
                      }
                    ]
                  },
                  { // survey-schemas/extra-survey-parts/*
                    path: routes.surveySchemas.extraSurveyParts.index,
                    handle: {
                      crumb: () => ({ content: 'extraSurveyParts.title', isTranslationKey: true })
                    },
                    children: [
                      {
                        index: true,
                        element: <ExtraSurveyParts translate={translate} />
                      },
                      { // survey-schemas/extra-survey-parts/:id/*
                        path: routes.surveySchemas.extraSurveyParts.surveyPart.index,
                        loader: ({ params }) => getExtraSurveyPart(params.surveyPartId),
                        handle: {
                          crumb: (data) => ({ content: data.title })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyPartView />
                          },
                          {
                            element: <RequireAuth permissions={[extraSurveyPartPermission(true)]} />,
                            children: [
                              { // survey-schemas/extra-survey-parts/:id/edit/*
                                path: routes.surveySchemas.extraSurveyParts.surveyPart.edit.index,
                                handle: {
                                  crumb: () => ({ content: 'actions.edit', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyPartEditor />
                                  },
                                  { // survey-schemas/extra-survey-parts/:id/edit/preview
                                    path: routes.surveySchemas.extraSurveyParts.surveyPart.edit.preview,
                                    element: <SchemaPartPreview />
                                  }
                                ]
                              },
                              { // survey-schemas/extra-survey-parts/:id/copy/*
                                path: routes.surveySchemas.extraSurveyParts.surveyPart.copy.index,
                                handle: {
                                  crumb: () => ({ content: 'actions.createNewCopy', isTranslationKey: true })
                                },
                                children: [
                                  {
                                    index: true,
                                    element: <SurveyPartEditor />
                                  },
                                  { // survey-schemas/extra-survey-parts/:id/copy/preview
                                    path: routes.surveySchemas.extraSurveyParts.surveyPart.copy.preview,
                                    element: <SchemaPartPreview />
                                  }
                                ]
                              }
                            ]
                          }
                        ]
                      },
                      { // survey-schemas/extra-survey-parts/create/*
                        path: routes.surveySchemas.extraSurveyParts.create.index,
                        element: <RequireAuth permissions={[extraSurveyPartPermission(true)]} />,
                        handle: {
                          crumb: () => ({ content: 'extraSurveyParts.createNew', isTranslationKey: true })
                        },
                        children: [
                          {
                            index: true,
                            element: <SurveyPartEditor />
                          },
                          { // survey-schemas/extra-survey-parts/create/preview
                            path: routes.surveySchemas.extraSurveyParts.create.preview,
                            element: <SchemaPartPreview />
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
], { basename: BASE_PATH });

const Main = ({ translate, lang }) => (
  <RouterProvider router={router(translate, lang)} />
);

Main.propTypes = {
  ...langType
};
Layout.propTypes = {
  ...langType
};

export default withLang(Main);
